import React, { useState, useEffect } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useI18n } from '../i18n';
import SubscriptionBanner from './SubscriptionBanner';
import { businessAccountsApi } from '../api';
import { showToast, showConfirm } from '../utils/toast';

// Helper function to safely format dates
function formatDate(dateValue) {
  if (!dateValue) return 'Unknown';
  
  let date;
  if (dateValue._seconds) {
    // Firebase Timestamp
    date = new Date(dateValue._seconds * 1000);
  } else {
    // Regular Date object or ISO string
    date = new Date(dateValue);
  }
  
  return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
}

export default function BusinessAccounts({ user, subStatus, onSubscribe }) {
  const { t } = useI18n();
  const { accounts, selectedAccountId, setSelectedAccountId, refreshAccounts } = useAccount();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Permission checks
  const canCreate = hasPermission('business_accounts:create');
  const canEdit = hasPermission('business_accounts:edit');
  const canDelete = hasPermission('business_accounts:delete');

  const handleCreate = () => {
    setEditingAccount(null);
    setFormData({ name: '', description: '' });
    setShowCreateModal(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name || '',
      description: account.description || ''
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    
    setLoading(true);
    try {
      if (editingAccount) {
        // Update existing account
        await businessAccountsApi.update(editingAccount.accountId, {
          name: formData.name.trim(),
          description: formData.description.trim()
        });
      } else {
        // Create new account
        await businessAccountsApi.create({
          name: formData.name.trim(),
          description: formData.description.trim()
        });
      }
      
      // Refresh accounts list
      await refreshAccounts();
      setShowCreateModal(false);
      setFormData({ name: '', description: '' });
      setEditingAccount(null);
    } catch (error) {
      console.error('Error saving business account:', error);
      showToast({ message: error?.message || 'Failed to save business account', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (accountId) => {
    const confirmed = await showConfirm({
      title: 'Delete business account?',
      message: 'Are you sure you want to delete this business account?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      destructive: true,
      variant: 'warning',
    });
    if (!confirmed) return;
    
    setLoading(true);
    try {
      await businessAccountsApi.remove(accountId);
      
      // Refresh accounts list
      await refreshAccounts();
    } catch (error) {
      console.error('Error deleting business account:', error);
      showToast({ message: error?.message || 'Failed to delete business account', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 mb-1">Business Accounts</h1>
                <p className="text-gray-600 text-sm sm:text-base">Manage your business units and track performance</p>
              </div>
            </div>
            {canCreate && (
              <button
                type="button"
                onClick={handleCreate}
                className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">Add Business Account</span>
                <span className="sm:hidden">Add</span>
              </button>
            )}
          </div>
        </div>

        {/* Subscription Banner */}
        <div className="mb-6">
          <SubscriptionBanner subStatus={subStatus} onSubscribe={onSubscribe} />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{accounts?.length || 0}</div>
                <div className="text-sm text-gray-600">Total Accounts</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {accounts.length}
                </div>
                <div className="text-sm text-gray-600">Total Accounts</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {selectedAccountId ? '✓' : '—'}
                </div>
                <div className="text-sm text-gray-600">Selected</div>
              </div>
            </div>
          </div>
        </div>

        {/* Helpful Description */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How Business Accounts Work</h3>
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  <strong>Multiple Businesses or Branches?</strong> Create separate business accounts for each unit to track their individual performance.
                </p>
                <p>
                  <strong>Individual Business View:</strong> Switch between accounts to see data for specific businesses. All new orders and expenses will be created for the selected business.
                </p>
                <p>
                  <strong>Overall Performance View:</strong> Use the toggle in Dashboard and P&L to see combined performance across all your businesses at once.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Current Selection Card */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-4 h-4 rounded-full bg-green-500" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900 text-lg">
                {accounts.find(acc => acc.accountId === selectedAccountId)?.name || 'No Account Selected'}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {accounts.find(acc => acc.accountId === selectedAccountId)?.description || 'Select an account to view its data'}
              </div>
            </div>
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-green-200 text-green-800">
              Active
            </div>
          </div>
        </div>

        {/* Accounts Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-green-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-green-100 bg-gradient-to-r from-gray-50 to-green-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Your Business Accounts</h3>
                <p className="text-sm text-gray-600">Tap to select an account</p>
              </div>
              <div className="text-sm text-gray-500">
                {accounts?.length || 0} accounts
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {accounts && accounts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accounts.map((account, index) => (
                  <div
                    key={account.accountId}
                    className={`group relative p-6 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-lg transform hover:-translate-y-1 ${
                      selectedAccountId === account.accountId
                        ? 'border-green-500 bg-green-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-green-300'
                    }`}
                    onClick={() => {
                      setSelectedAccountId(account.accountId);
                    }}
                  >
                    {/* Selection Indicator */}
                    {selectedAccountId === account.accountId && (
                      <div className="absolute top-3 right-3">
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Account Avatar */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                        selectedAccountId === account.accountId
                          ? 'bg-gradient-to-br from-green-500 to-green-600'
                          : 'bg-gradient-to-br from-gray-400 to-gray-500'
                      }`}>
                        {account.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">
                          {account.name || `Account ${index + 1}`}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {account.description || 'No description'}
                        </p>
                      </div>
                    </div>

                    {/* Account Details */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>
                          Created: {formatDate(account.createdDate)}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-1">
                        {canEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(account);
                            }}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors duration-200 rounded-lg hover:bg-green-50"
                            title="Edit account"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        
                        {canDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(account.accountId);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200 rounded-lg hover:bg-red-50"
                            title="Delete account"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                      
                      {selectedAccountId === account.accountId && (
                        <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          Active
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Business Accounts Yet</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {canCreate 
                    ? 'Create your first business account to start organizing your business units and tracking their performance.'
                    : 'You have view-only access to business accounts. Contact your administrator to create new business accounts.'
                  }
                </p>
                {canCreate && (
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Create Your First Account
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingAccount ? 'Edit Business Account' : 'Create Business Account'}
                </h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                    placeholder="e.g., Main Store, Online Shop, Branch 1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                    rows={3}
                    placeholder="Optional description for this business account"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!formData.name.trim() || loading}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-2 rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg"
                >
                  {loading ? 'Saving...' : (editingAccount ? 'Update Account' : 'Create Account')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
